/**
 * CommunicationChoices — the separate "how may we contact you" answers a
 * family gives when signing up, and the summary of them shown afterwards.
 *
 * ACCOUNT NOTICES (decided 2026-10-08): every account needs at least one way
 * to be reached about billing: email, text message, or a phone call. So the
 * three account choices are shown together, account email starts ticked, and
 * the last one that is ticked cannot be unticked (the box locks and says
 * why). Account email can be turned off once texts or a phone call is chosen.
 * The server applies the same rule to whatever a form sends.
 *
 * Why four: registration used to show two boxes, each covering email AND
 * texts. A parent who did not want texts left the "account" box blank, which
 * also left account email blank, and staff later read that as "this family
 * opted out of billing email" (Gymnastics of York, 2026-10-07).
 *
 *   Account & service email   starts CHECKED.
 *   Account & service texts   starts unchecked.
 *   Phone call                starts unchecked. Staff call; nothing automated.
 *   Promotional email         starts unchecked.
 *   Promotional texts         starts unchecked.
 *
 * Texts and promotional email need the person's own yes, so those three are
 * never pre-checked, and leaving one unchecked is never saved as a refusal.
 *
 * Used by BOTH public sign-up forms (the step-by-step wizard and the older
 * one-page form) so the wording cannot drift between them. The text-message
 * disclosures ("Msg & data rates may apply", HELP / STOP) are required next
 * to each text opt-in by the carrier review; keep them when rewording.
 */
import React from "react";
import { Checkbox } from "../ui";
import AccountNoticeMethodBoxes from "../common/AccountNoticeMethodBoxes";
import { ACCOUNT_NOTICE_RULE, keepAtLeastOne } from "../../utils/accountNoticeMethods";
import { useTheme } from "../../context/ThemeContext";
import { getPrimaryText, getSecondaryText } from "../../utils/themeUtils";
import { formatDateOrInstant } from "@mediatechgroup/calendar-date";

export interface CommunicationChoicesValue {
  accountEmail: boolean;
  accountTexts: boolean;
  /** "Call me about my account": staff call, nothing automated. */
  accountCall?: boolean;
  promotionalEmail: boolean;
  promotionalTexts: boolean;
}

export const DEFAULT_COMMUNICATION_CHOICES: CommunicationChoicesValue = {
  accountEmail: true,
  accountTexts: false,
  accountCall: false,
  promotionalEmail: false,
  promotionalTexts: false,
};

/** What the registration endpoints read (`communication_choices`). */
export interface CommunicationChoicesPayload {
  account_email: boolean;
  account_texts: boolean;
  account_call: boolean;
  promotional_email: boolean;
  promotional_texts: boolean;
}

export const communicationChoicesPayload = (
  value: Partial<CommunicationChoicesValue> | undefined
): CommunicationChoicesPayload => {
  // Only an explicit untick turns account email off, and only when texts or
  // a phone call was chosen instead: never zero ways to be reached.
  const account = keepAtLeastOne({
    email: value?.accountEmail !== false,
    text: value?.accountTexts === true,
    call: value?.accountCall === true,
  });
  return {
    account_email: account.email,
    account_texts: account.text,
    account_call: account.call,
    promotional_email: value?.promotionalEmail === true,
    promotional_texts: value?.promotionalTexts === true,
  };
};

/**
 * The two fields a server deployed before 2026-10-08 reads. Each of its boxes
 * meant email AND texts, so it is sent as "yes" only when the family said yes
 * to both; it can never turn on a channel the family did not choose.
 */
export const legacyOptInFields = (value: Partial<CommunicationChoicesValue> | undefined) => {
  const p = communicationChoicesPayload(value);
  return {
    transactional_opt_in: p.account_email && p.account_texts,
    marketing_opt_in: p.promotional_email && p.promotional_texts,
  };
};

/** The at-least-one rule, as the sign-up forms say it. */
export const ACCOUNT_NOTICE_NOTE = `${ACCOUNT_NOTICE_RULE} Email is on to start. You can turn it off once you choose text messages or a phone call.`;

interface Props {
  value: CommunicationChoicesValue;
  onChange: (next: CommunicationChoicesValue) => void;
  /** Called after any change (the wizard saves progress on it). */
  onAfterChange?: () => void;
}

const CommunicationChoices: React.FC<Props> = ({ value, onChange, onAfterChange }) => {
  const { isDarkMode } = useTheme();
  const change = (next: CommunicationChoicesValue) => {
    onChange(next);
    onAfterChange?.();
  };
  const set = (field: keyof CommunicationChoicesValue) => (e: React.ChangeEvent<HTMLInputElement>) =>
    change({ ...value, [field]: e.target.checked });
  const ACCOUNT_FIELD = { email: "accountEmail", text: "accountTexts", call: "accountCall" } as const;

  return (
    <fieldset className="mt-4" data-testid="communication-choices">
      <legend className={`text-sm font-semibold ${getPrimaryText(isDarkMode)}`}>
        How may we contact you?
      </legend>
      <p className={`text-xs mt-1 mb-3 ${getSecondaryText(isDarkMode)}`}>
        Choose each one separately. You can change any of these in your profile after you sign up.
      </p>

      <p className={`text-xs font-semibold mb-2 ${getPrimaryText(isDarkMode)}`}>About your account</p>
      <AccountNoticeMethodBoxes
        value={{
          email: value.accountEmail !== false,
          text: value.accountTexts === true,
          call: value.accountCall === true,
        }}
        onToggle={(method, checked) => change({ ...value, [ACCOUNT_FIELD[method]]: checked })}
        labels={{
          email: "Account and service email",
          text: "Account and service texts",
          call: "Phone call about my account",
        }}
        descriptions={{
          email: "Invoices, receipts, schedule changes, and other messages about your account, by email.",
          text: "The same updates by text message. Message frequency varies. Msg & data rates may apply. Reply HELP for help or STOP to cancel.",
          call: "A staff member calls you about billing instead. There is no automated call.",
        }}
      />
      <p className={`mt-3 mb-4 text-xs ${getSecondaryText(isDarkMode)}`} data-testid="account-notice-rule">
        {ACCOUNT_NOTICE_NOTE}
      </p>

      <p className={`text-xs font-semibold mb-2 ${getPrimaryText(isDarkMode)}`}>News and offers (optional)</p>
      <div className="space-y-3">
        <Checkbox
          label="Promotional email"
          description="News, new programs, and offers by email. Every promotional email has an unsubscribe link."
          checked={value.promotionalEmail}
          onChange={set("promotionalEmail")}
        />
        <Checkbox
          label="Promotional texts"
          description="News and offers by text message. Message frequency varies. Msg & data rates may apply. Reply HELP for help or STOP to cancel."
          checked={value.promotionalTexts}
          onChange={set("promotionalTexts")}
        />
      </div>

      <p className={`mt-3 text-[11px] ${getSecondaryText(isDarkMode)}`}>
        <a href="/sms-opt-in" target="_blank" rel="noreferrer" className="underline">More about text messages</a>
        {" · "}
        <a href="/privacy" target="_blank" rel="noreferrer" className="underline">Privacy</a>
        {" · "}
        <a href="/terms" target="_blank" rel="noreferrer" className="underline">Terms</a>
      </p>
    </fieldset>
  );
};

/** What the server recorded (`communication_choices` in the registration response). */
export interface RecordedCommunicationChoices extends Omit<CommunicationChoicesPayload, "account_call"> {
  /** Absent from a server that does not know about phone calls yet. */
  account_call?: boolean;
  /** The family turned account email off with nothing else chosen, so it stayed on. */
  account_email_kept?: boolean;
  recorded_at?: string | null;
  saved?: boolean;
}

/** One line per choice, in the order the form asks them. */
const choiceRows = (choices: Partial<CommunicationChoicesPayload>): [string, boolean][] => [
  ["Account and service email", choices.account_email === true],
  ["Account and service texts", choices.account_texts === true],
  ["Phone call about my account", choices.account_call === true],
  ["Promotional email", choices.promotional_email === true],
  ["Promotional texts", choices.promotional_texts === true],
];

/**
 * Before registering: the choices about to be sent, so the family can confirm
 * them on Review (they were made on the first step, several screens back).
 * `choices` is exactly what will be sent (communicationChoicesPayload).
 */
export const CommunicationChoicesReview: React.FC<{
  choices: CommunicationChoicesPayload;
  onChange?: () => void;
}> = ({ choices, onChange }) => {
  const { isDarkMode } = useTheme();
  return (
    <section
      aria-label="How we will contact you"
      data-testid="review-communication-choices"
      className={`rounded-xl shadow-lg p-4 sm:p-6 mb-4 border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
    >
      <h3 className={`text-lg font-semibold mb-3 ${getPrimaryText(isDarkMode)}`}>How we will contact you</h3>
      <ul className={`text-sm space-y-1 ${getSecondaryText(isDarkMode)}`}>
        {choiceRows(choices).map(([label, on]) => (
          <li key={label} className="flex justify-between gap-4">
            <span>{label}</span>
            <span className="font-medium">{on ? "Yes" : "No"}</span>
          </li>
        ))}
      </ul>
      {onChange && (
        <button
          type="button"
          onClick={onChange}
          className="mt-2 text-sm font-medium text-[var(--cc-link)] hover:underline"
        >
          Change
        </button>
      )}
    </section>
  );
};

/**
 * The confirmation: what the family chose, with the date it was saved. Shown
 * after registering. Renders nothing when there is nothing to show (the staff
 * add-member flows reach the same summary page without choices).
 */
export const CommunicationChoicesSummary: React.FC<{ choices?: RecordedCommunicationChoices | null }> = ({
  choices,
}) => {
  const { isDarkMode } = useTheme();
  if (!choices || typeof choices !== "object") return null;

  const rows = choiceRows(choices);
  // An instant: shown as the day it was for the person reading it.
  const savedOn = formatDateOrInstant(choices.recorded_at || null);

  return (
    <section aria-label="Your communication choices" data-testid="communication-choices-summary">
      <h3 className={`text-lg font-semibold ${getPrimaryText(isDarkMode)}`}>How we will contact you</h3>
      <p className={`text-xs mt-1 mb-3 ${getSecondaryText(isDarkMode)}`}>
        {choices.saved === false
          ? "These are the choices you made. We could not save them to your account just now, so please check them in your profile after you sign in."
          : savedOn
            ? `Your choices were saved on ${savedOn}. You can change them any time in your profile.`
            : "Your choices were saved. You can change them any time in your profile."}
      </p>
      <ul className={`text-sm space-y-1 ${getPrimaryText(isDarkMode)}`}>
        {rows.map(([label, on]) => (
          <li key={label} className="flex justify-between gap-4">
            <span>{label}</span>
            <span className="font-medium">{on ? "Yes" : "No"}</span>
          </li>
        ))}
      </ul>
      <p className={`mt-3 text-xs ${getSecondaryText(isDarkMode)}`} data-testid="account-notice-kept">
        {choices.account_email_kept
          ? `We kept account email on. ${ACCOUNT_NOTICE_RULE}`
          : ACCOUNT_NOTICE_RULE}
      </p>
    </section>
  );
};

export default CommunicationChoices;
